// The estimated loss on a market order and the monthly loss budget (owner,
// 2026-10-30): staff approve and buy at a loss up to loss_max_order_sar (200);
// above it nothing is bought — the product is corrected and the order refunded
// in full, and the trader may reorder at the corrected price. The estimate
// itself is computed in the database (market_order_loss_estimate); this file
// only reads it, and prices the corrected cart with the store's own math.

import { deliveredCart } from './marketDelivered';

export function lossLevel(loss, max) {
  if (loss == null) return 'unknown';
  if (Number(loss) <= 0) return 'none';
  return Number(loss) > Number(max) ? 'over' : 'accept';
}

export function budgetTone(pct) {
  if (pct == null) return 'ok';
  return pct >= 100 ? 'red' : pct >= 80 ? 'amber' : 'ok';
}

const KG_FROM = {
  price: ['المستخدم في السعر', 'used in the price'],
  gemini: ['تقدير Gemini للمنتج', "Gemini's estimate"],
  sibling_gemini: ['أقل تقدير Gemini بين خيارات المنتج', "lowest Gemini estimate among the variants"],
};
export const evidenceLabel = (from, isAr) => (KG_FROM[from] || [from, from])[isAr ? 0 : 1];

// The database's refusals at approval, in words staff act on.
export function approveErrorText(error, isAr) {
  const e = String(error || '');
  let m = e.match(/loss_above_limit: ([\d.]+) > ([\d.]+)/);
  if (m) return isAr ? `الخسارة التقديرية ${m[1]} ر.س فوق الحد ${m[2]} — لا شراء. استخدم «تصحيح واسترداد».` : `Estimated loss ${m[1]} SAR is above the ${m[2]} limit — no purchase. Use "Correct and refund".`;
  m = e.match(/loss_budget_exceeded: ([\d.]+) \+ ([\d.]+) > ([\d.]+)/);
  if (m) return isAr ? `ميزانية الخسارة الشهرية: ${m[1]} + ${m[2]} فوق ${m[3]} ر.س — اكتب سببًا (10 أحرف على الأقل) لتجاوزها.` : `Monthly loss budget: ${m[1]} + ${m[2]} is over ${m[3]} SAR — write a reason (10+ characters) to override.`;
  if (/loss_estimate_unavailable/.test(e)) return isAr ? 'تعذّر حساب الخسارة التقديرية (منتج بلا بيانات) — راجع الطلب قبل الاعتماد.' : 'The loss estimate is unavailable (a product without data) — review before approving.';
  return e;
}

// The same cart re-priced with the store's math after the correction.
// lines: [{ product_id, sku_key, qty, unit_sar }]; delivered: market_delivered rows; cfg: market_public_state().delivered
export function correctedTotal(lines, delivered, cfg, method) {
  const byId = Object.fromEntries((delivered || []).map((d) => [d.product_id, d]));
  const dl = [];
  for (const l of lines) {
    const info = byId[l.product_id];
    const sku = info?.skus?.find((v) => v.key === (l.sku_key ?? ''));
    if (!info || !sku) return null;
    dl.push({ unit_sar: Number(l.unit_sar), qty: l.qty, sku, cls: info.air_class });
  }
  if (!dl.length || !cfg) return null;
  const c = deliveredCart(dl, cfg);
  const o = method === 'sea' && c.sea.available ? c.sea : c.air;
  return o.total_sar;
}

// The message staff send the trader (WhatsApp), Arabic. No source names, no internal figures.
export function correctionMessage({ orderNo, paid, corrected, links }) {
  return [
    `مرحبًا، بخصوص طلبك ${orderNo} في معبر:`,
    'راجعنا وزن وحجم المنتج فوجدنا أن سعر الشحن المعروض كان أقل من الحقيقي، فصحّحناه.',
    `ألغينا الطلب وسنعيد لك المبلغ كاملًا (${paid} ر.س).`,
    corrected != null ? `السعر الصحيح لنفس الطلب الآن: ${corrected} ر.س شامل الشحن والجمارك.` : null,
    links?.length ? `إن رغبت بالطلب من جديد:\n${links.join('\n')}` : null,
    'نعتذر عن الإزعاج، ونشكر ثقتك.',
  ].filter(Boolean).join('\n');
}

// The integrity hold's flags (market_order_line_flags), as the hold email words them.
const FLAG = {
  sibling_weight: ['وزن مختلف جدًا عن بقية الخيارات', 'Weight far from the other variants'],
  similar_size_price: ['مقاس مشابه بسعر مختلف جدًا', 'Similar size at a very different price'],
  type_weight: ['وزن غير منطقي لنوع المنتج', 'Implausible weight for the product type'],
  over_20000_sar: ['سعر الوحدة فوق 20,000 ر.س', 'Unit price above 20,000 SAR'],
  sea_air_10x: ['البحري أرخص من الجوي بأكثر من 10×', 'Sea more than 10× cheaper than air'],
  absurd_shipping: ['الشحن أكثر من 100× سعر المصنع', 'Shipping over 100× the factory price'],
  weight_below_estimate: ['الوزن أقل بكثير من تقدير Gemini (رقم افتراضي من المورد؟)', "Weight far below Gemini's estimate (a placeholder?)"],
  no_product_data: ['لا بيانات للمنتج', 'No product data'], no_variant_data: ['لا بيانات للخيار', 'No variant data'],
};
export const flagLabel = (f, isAr) => (f.startsWith('data_hold:') ? `${isAr ? 'محجوب' : 'Held'}: ${f.slice(10)}` : (FLAG[f] || [f, f])[isAr ? 0 : 1]);
